import React from 'react';

import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import messages from './Messages';
import {
  EndTime,
  ReportViewType,
  ITags,
  ITagView,
} from 'store/providers/providerTypes';
import cloneDeep from 'lodash/cloneDeep';
import Button from '../Button/Button';
import Input from '../Input/Input';
import { IOpenItem } from 'store/entity/IOpenItem';
import { IProviderSurveyReport } from '../../store/survey/surveyTypes';
import { lightGray, red } from '../../utils/style/colors';
import Icon from '../Icon';

interface IComponentProps {
  surveyReport: IOpenItem;
  updateSurveyReportItem: (item: IProviderSurveyReport) => Promise<any>;
  tags: ITags;
}
interface IComponentStates {
  saving: boolean;
  defaultViewType: ReportViewType;
  forceFilter?: string;
  defaultEndTime: EndTime;
  groupByTags: number[];
  mandatorySearchTags: number[];
  filterOptions: {
    filterSelectTagRoots?: number[];
    filterTitle?: string;
  }[];
}
const viewTypes: IOption[] = [
  { value: 'List', label: 'لیست' },
  { value: 'FullScreen', label: 'تمام صفحه' },
  { value: 'Dashboard', label: 'داشبورد' },
  { value: 'BSC', label: 'جدول (کارت امتیازی متوازن)' },
];

const endTimes: IOption[] = [
  { value: 'Yesterday', label: 'دیروز' },
  { value: 'Today', label: 'امروز' },
  { value: 'EndOfWeek', label: 'آخر هفته' },
  { value: 'EndOfMonth', label: 'آخر ماه' },
  { value: 'EndOfSeason', label: 'آخر فصل' },
  { value: 'EndOfHalfYear', label: 'آخر نیم سال' },
  { value: 'EndOfYear', label: 'آخر سال' },
  { value: 'LastMonth', label: 'ماه قبل' },
  { value: 'LastSeason', label: 'فصل قبل' },
  { value: 'LastHalfYear', label: 'نیم سال قبل' },
  { value: 'LastYear', label: 'سال قبل' },
];

class ReportConfig extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state = {
    saving: false,
    defaultViewType:
      (this.props.surveyReport!.item.data as IProviderSurveyReport)
        .defaultViewType || 'List',
    groupByTags:
      (this.props.surveyReport!.item.data as IProviderSurveyReport)
        .groupByTags || [],
    mandatorySearchTags:
      (this.props.surveyReport!.item.data as IProviderSurveyReport)
        .mandatorySearchTags || [],
    filterOptions:
      (this.props.surveyReport!.item.data as IProviderSurveyReport)
        .filterOptions || [],
    defaultEndTime:
      (this.props.surveyReport!.item.data as IProviderSurveyReport)
        .defaultEndTime || 'Today',
    forceFilter: (this.props.surveyReport!.item.data as IProviderSurveyReport)
      .forceFilter,
  };
  handleChangeDefaultViewType = (values: IOption[]) => {
    this.setState({
      defaultViewType: (values[0].value.toString() as ReportViewType) || 'List',
    });
  };
  handleChangeDefaultEndTime = (values: IOption[]) => {
    this.setState({
      defaultEndTime: (values[0].value.toString() as EndTime) || 'EndOfMonth',
    });
  };
  handleChangeGroupBy = (values: IOption[]) => {
    this.setState({
      groupByTags: values.map((v) => Number(v.value)),
    });
  };
  handleChangeMandatorySearchTagsTags = (values: IOption[]) => {
    this.setState({
      mandatorySearchTags: values.map((v) => Number(v.value)),
    });
  };
  handleChangeFilterSelectTagRoots = (index: number) => (values: IOption[]) => {
    const filterOptions = cloneDeep(this.state.filterOptions);
    filterOptions[index].filterSelectTagRoots = values.map((v) =>
      Number(v.value)
    );
    this.setState({
      filterOptions,
    });
  };
  handleChangeFilterTitle = (index: number) => (title: string) => {
    const filterOptions = cloneDeep(this.state.filterOptions);
    filterOptions[index].filterTitle = title;
    this.setState({
      filterOptions,
    });
  };
  handleChangeForceFilterWithTag = (values: IOption[]) => {
    this.setState({
      forceFilter: values.map((v) => 'T' + v.value).join(','),
    });
  };
  handleSave = () => {
    const openItem = this.props.surveyReport as IOpenItem;
    const item = cloneDeep(openItem.item);
    (item.data as IProviderSurveyReport).defaultViewType =
      this.state.defaultViewType;
    (item.data as IProviderSurveyReport).groupByTags = this.state.groupByTags;
    (item.data as IProviderSurveyReport).filterOptions =
      this.state.filterOptions;
    // remove old data:
    delete (item.data as IProviderSurveyReport)['filterTitle'];
    delete (item.data as IProviderSurveyReport)['filterSelectTagRoots'];

    (item.data as IProviderSurveyReport).mandatorySearchTags =
      this.state.mandatorySearchTags;
    (item.data as IProviderSurveyReport).defaultEndTime =
      this.state.defaultEndTime;
    (item.data as IProviderSurveyReport).forceFilter = this.state.forceFilter;
    this.setState({ saving: true });
    this.props
      .updateSurveyReportItem(item.data as IProviderSurveyReport)
      .then(() => {
        this.setState({ saving: false });
      });
  };

  render() {
    const { tags } = this.props;
    const {
      saving,
      defaultEndTime,
      defaultViewType,
      groupByTags,
      mandatorySearchTags,
      filterOptions,
      forceFilter,
    } = this.state;
    const groupByTagsTags = [] as IOption[];
    const mandatorySearchTagsTags = [] as IOption[];
    const filterSelectTagRootsTags = [] as IOption[][];
    const forceFilterTagValues = [] as IOption[];
    const options = Object.values(tags.data)
      .filter((tag: ITagView) => !tag.deleted)
      .reduce((tmp: IOption[], tag) => {
        const label = tag.text;
        const id = Number(tag.id);
        if (groupByTags.includes(id))
          groupByTagsTags.push({ value: id, label });
        if (mandatorySearchTags.includes(id))
          mandatorySearchTagsTags.push({ value: id, label });
        filterOptions.forEach((filterOption, index) => {
          filterSelectTagRootsTags.push([]);
          if (filterOption.filterSelectTagRoots?.includes(id)) {
            filterSelectTagRootsTags[index].push({ value: id, label });
          }
        });
        if (forceFilter?.split(',').includes('T' + id))
          forceFilterTagValues.push({ value: id, label });
        tmp.push({ value: id, label });
        return tmp;
      }, []);
    groupByTagsTags.sort((a, b) => {
      return (
        groupByTags.indexOf(Number(a.value)) -
        groupByTags.indexOf(Number(b.value))
      );
    });
    return (
      <div>
        <Select
          onChangeValues={this.handleChangeDefaultViewType}
          options={viewTypes}
          values={[viewTypes.find((it) => it.value === defaultViewType)!]}
          isRequired={true}
          allowRemove={false}
          label={messages.defaultViewType}
        />

        <Select
          onChangeValues={this.handleChangeMandatorySearchTagsTags}
          options={options}
          values={mandatorySearchTagsTags}
          isRequired={false}
          allowRemove={true}
          multiChoice={true}
          label={messages.reportSearchMandatoryTags}
        />
        <Select
          onChangeValues={this.handleChangeDefaultEndTime}
          options={endTimes}
          values={[endTimes.find((it) => it.value === defaultEndTime)!]}
          isRequired={true}
          allowRemove={false}
          label={messages.defaultEndTime}
        />
        <Select
          onChangeValues={this.handleChangeGroupBy}
          options={options}
          values={groupByTagsTags}
          isRequired={false}
          allowRemove={true}
          multiChoice={true}
          label={messages.groupInBSC}
        />
        <div>
          <Button
            onClick={() => {
              this.setState({
                filterOptions: [
                  ...filterOptions,
                  {
                    filterSelectTagRoots: [],
                    filterTitle: '',
                  },
                ],
              });
            }}
          >
            {messages.addFilter}
          </Button>
          {filterOptions.map((filterOption, index) => (
            <div
              key={index}
              style={{
                border: `1px solid ${lightGray}`,
                borderRadius: '10px',
                padding: '10px',
                margin: '5px 0',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                <Input
                  id={'filterTitle'}
                  type="text"
                  onChange={(e) =>
                    this.handleChangeFilterTitle(index)(e.target.value)
                  }
                  style={{ flexGrow: '1' }}
                  value={filterOption.filterTitle}
                  label={messages.filterTitle}
                />
                <Icon
                  type={'trash'}
                  color={red}
                  onClick={() => {
                    const ret = [...filterOptions];
                    ret.splice(index, 1);
                    this.setState({
                      filterOptions: ret,
                    });
                  }}
                />
              </div>
              <Select
                onChangeValues={this.handleChangeFilterSelectTagRoots(index)}
                options={options}
                values={filterSelectTagRootsTags[index]}
                isRequired={false}
                allowRemove={true}
                multiChoice={true}
                label={messages.filterSelectTagRoots}
              />
            </div>
          ))}
        </div>

        <Select
          onChangeValues={this.handleChangeForceFilterWithTag}
          options={options}
          values={forceFilterTagValues}
          isRequired={false}
          allowRemove={true}
          multiChoice={true}
          label={messages.forceFilter}
        />
        <Button color="green" onClick={this.handleSave} loading={saving}>
          {messages.save}
        </Button>
      </div>
    );
  }
}

export default ReportConfig;
